import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  ButtonGroup,
  CircularProgress,
  Alert,
  Dialog,
  Snackbar,
  Slide,
  Skeleton,
} from '@mui/material'
import {
  DragIndicator,
  PhotoSizeSelectSmall,
  PhotoSizeSelectLarge,
  PhotoSizeSelectActual,
  Edit as EditIcon,
  AddCircleOutlined,
  KeyboardArrowUp,
  KeyboardArrowDown,
  HelpOutlined,
  Visibility,
} from '@mui/icons-material'
import { PinturaEditor } from '@pqina/react-pintura'
import { getEditorDefaults } from '@pqina/pintura'
import '@pqina/pintura/pintura.css'
import axios from 'axios'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd'
import { useQueryClient } from '@tanstack/react-query'
import { DeleteIcon } from 'src/v3/core/icons'
import ConfirmAction from 'src/v3/components/confirm-action'
import { useCdnImageRetry } from 'src/v3/hooks/use-cdn-image-retry'
import BlockCreationPalette from './components/block-creation-palette'
import EmptyState from './components/empty-state'
import EnhancedHelpPanel from './components/enhanced-help-panel'
import ShowNotesPreviewModal from './components/show-notes-preview-modal'
import {
  BlockBuilderProps,
  ShowNotesBlock,
  ShowNotesTextBlock,
  ShowNotesImageBlock,
  ShowNotesPDFBlock,
} from './types'
import { TextBlock } from './blocks/text-block'
import { extractCitationTokens, getCitationBody } from './blocks/citation-node'
import {
  useShowNotesBlocks,
  useShowNotesBlocksRendered,
  useCreateBlock,
  useDeleteBlock,
  useReorderBlocks,
  useUpdateBlock,
  useUploadBlockImage,
  useAutoSave,
} from './hooks'
import { useUploadBlockPDF } from './hooks/use-upload-block-pdf'
import { usePDFViewerUrl } from './hooks/use-pdf-viewer-url'
import { PDFViewer } from './components/pdf-viewer'

function BlockSkeleton({ blockType }: { blockType: 'text' | 'image' | 'pdf' }) {
  return (
    <Card
      elevation={2}
      sx={{
        mb: 2,
        opacity: 0.7,
        animation: 'pulse 1.5s ease-in-out infinite',
        '@keyframes pulse': {
          '0%': { opacity: 0.7 },
          '50%': { opacity: 0.4 },
          '100%': { opacity: 0.7 },
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Block Header Skeleton */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="rectangular" width={24} height={24} />
            <Skeleton variant="text" width={80} height={20} />
          </Box>
          <Skeleton variant="circular" width={32} height={32} />
        </Box>

        {/* Block Content Skeleton */}
        {blockType === 'text' ? (
          <Box>
            <Skeleton variant="text" width="100%" height={40} />
            <Skeleton variant="text" width="85%" height={20} sx={{ mt: 1 }} />
            <Skeleton variant="text" width="92%" height={20} sx={{ mt: 0.5 }} />
            <Skeleton variant="text" width="78%" height={20} sx={{ mt: 0.5 }} />
          </Box>
        ) : blockType === 'pdf' ? (
          <Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}
            >
              <Skeleton variant="text" width="40%" height={24} />
              <Skeleton variant="text" width="20%" height={24} />
            </Box>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={600}
              sx={{ borderRadius: 1, mb: 2 }}
            />
            <Skeleton
              variant="text"
              width="30%"
              height={36}
              sx={{ mx: 'auto' }}
            />
          </Box>
        ) : (
          <Box>
            <Skeleton
              variant="rectangular"
              width="60%"
              height={200}
              sx={{ mx: 'auto', borderRadius: 1, mb: 2 }}
            />
            <Skeleton
              variant="text"
              width="40%"
              height={36}
              sx={{ mx: 'auto' }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

function TextBlockContent({
  block,
  onChange,
  existingCitations,
  basePath,
}: {
  block: ShowNotesTextBlock
  onChange: (textBlock: ShowNotesBlock) => void
  existingCitations: string[]
  basePath: string
}) {
  let { autoSave, isSaving, saveError } = useAutoSave({ delay: 2000, basePath })

  let handleTextBlockChange = (updatedBlock: ShowNotesTextBlock) => {
    onChange(updatedBlock)
    // Trigger debounced auto-save
    autoSave(updatedBlock)
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Auto-save status indicator */}
      {isSaving && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 1,
            px: 1,
            py: 0.5,
          }}
        >
          <CircularProgress size={12} sx={{ mr: 0.5 }} />
          <Typography variant="caption" sx={{ color: 'white' }}>
            Saving...
          </Typography>
        </Box>
      )}
      {/* Auto-save error indicator */}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to save: {saveError.message}
        </Alert>
      )}
      <TextBlock
        block={block}
        onChange={handleTextBlockChange}
        placeholder="Start writing your text content..."
        existingCitations={existingCitations}
      />
    </Box>
  )
}

function ImageBlockContent({
  block,
  onChange,
  basePath,
}: {
  block: ShowNotesImageBlock
  onChange: (imageBlock: ShowNotesBlock) => void
  basePath: string
}) {
  let queryClient = useQueryClient()
  let uploadImageMutation = useUploadBlockImage(basePath)
  let updateBlockMutation = useUpdateBlock(basePath)

  let handleCaptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let updatedBlock: ShowNotesImageBlock = {
      ...block,
      caption: event.target.value,
      updatedTime: new Date(),
    }
    onChange(updatedBlock)

    // Auto-save caption changes
    updateBlockMutation.mutate(updatedBlock)
  }

  let [showControls, setShowControls] = useState(false)
  let [pinturaOpen, setPinturaOpen] = useState(false)
  let [pinturaFile, setPinturaFile] = useState<File | Blob | null>(null)
  let [loadingExistingImage, setLoadingExistingImage] = useState(false)
  let {
    src: cdnImageSrc,
    onError: cdnImageOnError,
    isRetrying: imageRetrying,
    hasError: imageCdnError,
  } = useCdnImageRetry(
    block.imageUrl
      ? `${import.meta.env.VITE_CLOUDFRONT_URL}/images/${block.imageUrl.replace('originals', 'resized')}`
      : null,
  )

  let handleWidthChange = (newPercent: number) => {
    let updatedBlock: ShowNotesImageBlock = {
      ...block,
      widthPercent: newPercent,
      updatedTime: new Date(),
    }
    onChange(updatedBlock)

    // Auto-save width changes
    updateBlockMutation.mutate(updatedBlock)
  }

  let handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    let file = event.target.files?.[0]
    if (file) {
      setPinturaFile(file)
      setPinturaOpen(true)
    }
    // Clear the input
    // eslint-disable-next-line fp/no-mutation, no-param-reassign
    event.target.value = ''
  }

  let handleEditExisting = async () => {
    if (!block.imageUrl) return

    setLoadingExistingImage(true)
    try {
      // Use S3 URL for editing (CORS-enabled) instead of CloudFront
      // S3 bucket allows CORS, while CloudFront does not
      let s3BaseUrl = 'https://foamfrat-public.s3.us-east-2.amazonaws.com'
      // Try resized first, fall back to original if not available
      let resizedUrl = `${s3BaseUrl}/images/${block.imageUrl.replace('originals', 'resized')}`
      let originalUrl = `${s3BaseUrl}/images/${block.imageUrl}`

      let response = await axios
        .get(resizedUrl, { responseType: 'blob' })
        .catch(() =>
          // Resized not available yet, use original
          axios.get(originalUrl, { responseType: 'blob' }),
        )

      setPinturaFile(response.data)
      setPinturaOpen(true)
    } catch {
      // Failed to load existing image for editing
    } finally {
      setLoadingExistingImage(false)
    }
  }

  let handlePinturaProcess = async (result: any) => {
    try {
      // Upload the processed image
      await uploadImageMutation.mutateAsync({
        blockID: block.id,
        file: result.dest,
      })

      // Refetch blocks to get the updated S3 key from backend and wait for it to complete
      await queryClient.refetchQueries({
        queryKey: ['showNotesBlocks', basePath, block.showNotesID],
      })

      setPinturaOpen(false)
      setPinturaFile(null)
    } catch {
      // Failed to process image
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: (theme) => (theme.palette.background as any).paperDark,
        borderRadius: 1,
        p: 2,
      }}
    >
      {/* Upload progress indicator */}
      {uploadImageMutation.isPending && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Uploading image...
          </Typography>
        </Box>
      )}
      {/* Upload error indicator */}
      {uploadImageMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to upload image: {uploadImageMutation.error?.message}
        </Alert>
      )}
      {block.imageUrl ? (
        <>
          {/* Image Display - Centered with hover controls */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              position: 'relative',
            }}
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
          >
            {/* Image Controls - On Hover */}
            {showControls && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10,
                  display: 'flex',
                  gap: 0.5,
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: 1,
                  p: 0.5,
                }}
              >
                <ButtonGroup size="small" variant="contained" sx={{ mr: 1 }}>
                  <IconButton
                    size="small"
                    aria-label="Set image width to 33%"
                    onClick={() => handleWidthChange(33)}
                    color={block.widthPercent <= 33 ? 'primary' : 'default'}
                    sx={{ color: 'white', minWidth: 'auto', px: 1 }}
                  >
                    <PhotoSizeSelectSmall fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Set image width to 66%"
                    onClick={() => handleWidthChange(66)}
                    color={
                      block.widthPercent > 33 && block.widthPercent <= 66
                        ? 'primary'
                        : 'default'
                    }
                    sx={{ color: 'white', minWidth: 'auto', px: 1 }}
                  >
                    <PhotoSizeSelectLarge fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Set image width to 100%"
                    onClick={() => handleWidthChange(100)}
                    color={block.widthPercent > 66 ? 'primary' : 'default'}
                    sx={{ color: 'white', minWidth: 'auto', px: 1 }}
                  >
                    <PhotoSizeSelectActual fontSize="small" />
                  </IconButton>
                </ButtonGroup>

                <IconButton
                  size="small"
                  aria-label="Edit image"
                  onClick={handleEditExisting}
                  disabled={loadingExistingImage}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                    minWidth: 'auto',
                    px: 1,
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.9)',
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {imageRetrying ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Processing image…
                </Typography>
              </Box>
            ) : (
              <img
                src={
                  imageCdnError && block.imageUrl
                    ? `${import.meta.env.VITE_CLOUDFRONT_URL}/images/${block.imageUrl}`
                    : (cdnImageSrc ?? undefined)
                }
                alt={block.altText}
                onError={imageCdnError ? undefined : cdnImageOnError}
                style={{
                  width: `${block.widthPercent}%`,
                  height: 'auto',
                  borderRadius: '8px',
                }}
                draggable={false}
              />
            )}
          </Box>

          {/* Caption */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <TextField
              label="Caption"
              value={block.caption}
              onChange={handleCaptionChange}
              placeholder="Add a caption..."
              variant="outlined"
              size="small"
              sx={{
                width: '100%',
                maxWidth: '600px',
              }}
            />
          </Box>
        </>
      ) : (
        <Box
          component="label"
          role="button"
          tabIndex={uploadImageMutation.isPending ? -1 : 0}
          aria-label="Upload an image"
          onKeyDown={(e) => {
            if (
              !uploadImageMutation.isPending &&
              (e.key === 'Enter' || e.key === ' ')
            ) {
              e.preventDefault()
              ;(
                e.currentTarget.querySelector('input') as HTMLInputElement
              )?.click()
            }
          }}
          sx={{
            display: 'block',
            p: 5,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            cursor: uploadImageMutation.isPending ? 'not-allowed' : 'pointer',
            '&:hover': uploadImageMutation.isPending
              ? {}
              : { borderColor: 'text.secondary' },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
        >
          <AddCircleOutlined
            sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }}
          />
          <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
            Click to upload an image
          </Typography>
          <Typography variant="caption" color="text.disabled">
            PNG, JPG — up to 10 MB
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            hidden
            disabled={uploadImageMutation.isPending}
          />
        </Box>
      )}
      {/* Pintura Image Editor Dialog */}
      <Dialog
        open={pinturaOpen}
        onClose={() => setPinturaOpen(false)}
        maxWidth="lg"
        fullWidth
        aria-labelledby="pintura-dialog-title"
        sx={{ '& .MuiDialog-paper': { height: '90vh' } }}
      >
        <Box sx={{ height: '100%', p: 2 }}>
          <Typography
            id="pintura-dialog-title"
            variant="h6"
            sx={{ textAlign: 'center', mb: 2 }}
          >
            Edit Image
          </Typography>
          <Box sx={{ height: 'calc(100% - 60px)' }}>
            {pinturaFile && (
              <PinturaEditor
                {...getEditorDefaults()}
                src={pinturaFile}
                onProcess={handlePinturaProcess}
                onClose={() => setPinturaOpen(false)}
              />
            )}
          </Box>
        </Box>
      </Dialog>
    </Box>
  )
}

function PDFBlockContent({
  block,
  onChange,
  basePath,
}: {
  block: ShowNotesPDFBlock
  onChange: (pdfBlock: ShowNotesBlock) => void
  basePath: string
}) {
  let queryClient = useQueryClient()
  let uploadPDFMutation = useUploadBlockPDF(basePath)
  let updateBlockMutation = useUpdateBlock(basePath)

  // Fetch presigned URL for viewing (skip if no PDF uploaded yet)
  let {
    data: presignedUrl,
    isLoading: loadingUrl,
    error: urlError,
  } = usePDFViewerUrl(block.pdfUrl ? block.id : '', basePath)

  let handleCaptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let updatedBlock: ShowNotesPDFBlock = {
      ...block,
      caption: event.target.value,
      updatedTime: new Date(),
    }
    onChange(updatedBlock)

    // Auto-save caption changes
    updateBlockMutation.mutate(updatedBlock)
  }

  let handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let file = event.target.files?.[0]
    if (file && file.type === 'application/pdf') {
      try {
        await uploadPDFMutation.mutateAsync({
          blockID: block.id,
          file,
        })

        // Invalidate cached presigned URL for this block
        queryClient.invalidateQueries({
          queryKey: ['pdfViewerUrl', basePath, block.id],
        })

        // Refetch blocks to get updated data from backend and wait for it to complete
        await queryClient.refetchQueries({
          queryKey: ['showNotesBlocks', basePath, block.showNotesID],
        })
      } catch {
        // Mutation handles error logging
      }
    }
    // Clear the input
    // eslint-disable-next-line fp/no-mutation, no-param-reassign
    event.target.value = ''
  }

  return (
    <Box
      sx={{
        backgroundColor: (theme) => (theme.palette.background as any).paperDark,
        borderRadius: 1,
        p: 2,
      }}
    >
      {/* Upload progress indicator */}
      {uploadPDFMutation.isPending && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Uploading PDF...
          </Typography>
        </Box>
      )}

      {/* Upload error indicator */}
      {uploadPDFMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to upload PDF: {uploadPDFMutation.error?.message}
        </Alert>
      )}

      {block.pdfUrl ? (
        <>
          <Box sx={{ mb: 2 }}>
            <PDFViewer
              block={block}
              presignedUrl={presignedUrl}
              loading={loadingUrl}
              error={urlError}
              width={700}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              component="label"
              disabled={uploadPDFMutation.isPending}
            >
              Replace PDF
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileSelect}
                hidden
                disabled={uploadPDFMutation.isPending}
              />
            </Button>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <TextField
              value={block.caption || ''}
              onChange={handleCaptionChange}
              placeholder="Add a caption (optional)..."
              variant="outlined"
              size="small"
              sx={{
                width: '100%',
                maxWidth: '600px',
              }}
            />
          </Box>
        </>
      ) : (
        <Box
          component="label"
          role="button"
          tabIndex={uploadPDFMutation.isPending ? -1 : 0}
          aria-label="Upload a PDF"
          onKeyDown={(e) => {
            if (
              !uploadPDFMutation.isPending &&
              (e.key === 'Enter' || e.key === ' ')
            ) {
              e.preventDefault()
              ;(
                e.currentTarget.querySelector('input') as HTMLInputElement
              )?.click()
            }
          }}
          sx={{
            display: 'block',
            p: 5,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            cursor: uploadPDFMutation.isPending ? 'not-allowed' : 'pointer',
            '&:hover': uploadPDFMutation.isPending
              ? {}
              : { borderColor: 'text.secondary' },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
        >
          <AddCircleOutlined
            sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }}
          />
          <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
            Click to upload a PDF
          </Typography>
          <Typography variant="caption" color="text.disabled">
            PDF files — up to 50 MB
          </Typography>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileSelect}
            hidden
            disabled={uploadPDFMutation.isPending}
          />
        </Box>
      )}
    </Box>
  )
}

interface BlockContainerProps {
  block: ShowNotesBlock
  onBlockChange: (block: ShowNotesBlock) => void
  onBlockDelete: (blockID: string) => void
  onMoveUp: () => void
  onMoveDown: () => void
  isFirst: boolean
  isLast: boolean
  index: number
  dialogStatus: string
  setDialogStatus: (status: string) => void
  basePath: string
  disabled?: boolean
  isDeleting?: boolean
  existingCitations: string[]
}

function BlockContainer({
  block,
  onBlockChange,
  onBlockDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  index,
  dialogStatus,
  setDialogStatus,
  basePath,
  disabled = false,
  isDeleting = false,
  existingCitations,
}: BlockContainerProps) {
  // Guard against undefined blocks
  if (!block?.id) {
    return null
  }

  let handleDelete = async (): Promise<unknown> => {
    onBlockDelete(block.id)
    return Promise.resolve()
  }

  return (
    <Draggable draggableId={block.id} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          elevation={snapshot.isDragging ? 4 : 2}
          sx={{
            mb: 2,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            ...(isDeleting && {
              animation: 'deleteOut 0.3s ease-in-out forwards',
              '@keyframes deleteOut': {
                '0%': {
                  transform: 'scale(1) translateX(0)',
                  opacity: 1,
                  height: 'auto',
                  marginBottom: '16px',
                },
                '50%': {
                  transform: 'scale(0.95) translateX(-20px)',
                  opacity: 0.5,
                },
                '100%': {
                  transform: 'scale(0.9) translateX(-40px)',
                  opacity: 0,
                  height: 0,
                  marginBottom: 0,
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              },
            }),
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            {/* Block Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  {...provided.dragHandleProps}
                  size="small"
                  aria-label={`Drag to reorder ${block.blockType} block`}
                  sx={{
                    cursor: 'grab',
                    color: 'text.disabled',
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <DragIndicator fontSize="small" />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    color: 'text.secondary',
                  }}
                >
                  {block.blockType === 'text'
                    ? 'TEXT'
                    : block.blockType === 'image'
                      ? 'IMAGE'
                      : 'PDF'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {!isFirst && (
                  <Tooltip title="Move up">
                    <IconButton
                      size="small"
                      aria-label="Move block up"
                      disabled={disabled}
                      onClick={onMoveUp}
                      sx={{ color: 'text.disabled' }}
                    >
                      <KeyboardArrowUp fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {!isLast && (
                  <Tooltip title="Move down">
                    <IconButton
                      size="small"
                      aria-label="Move block down"
                      disabled={disabled}
                      onClick={onMoveDown}
                      sx={{ color: 'text.disabled' }}
                    >
                      <KeyboardArrowDown fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    aria-label={`Delete ${block.blockType} block`}
                    disabled={disabled}
                    onClick={() => setDialogStatus(`delete-${block.id}`)}
                    sx={{ color: 'text.disabled' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <ConfirmAction
                executeAction={handleDelete}
                open={dialogStatus}
                setOpen={setDialogStatus}
                openText={`delete-${block.id}`}
                title="Delete Block"
                text={`Are you sure you want to delete this ${block.blockType} block?`}
              />
            </Box>

            {/* Block Content */}
            {block.blockType === 'text' ? (
              <TextBlockContent
                block={block as ShowNotesTextBlock}
                onChange={onBlockChange}
                existingCitations={existingCitations}
                basePath={basePath}
              />
            ) : block.blockType === 'image' ? (
              <ImageBlockContent
                block={block as ShowNotesImageBlock}
                onChange={onBlockChange}
                basePath={basePath}
              />
            ) : (
              <PDFBlockContent
                block={block as ShowNotesPDFBlock}
                onChange={onBlockChange}
                basePath={basePath}
              />
            )}
          </CardContent>
        </Card>
      )}
    </Draggable>
  )
}

export function BlockBuilder({
  showNotesID,
  basePath,
  showNotesName,
  showNotesDescription,
  onBlocksChange,
  className,
}: BlockBuilderProps) {
  let [dialogStatus, setDialogStatus] = useState<string>('')
  let [openAddBlockAt, setOpenAddBlockAt] = useState<number | null>(null)
  let [helpPanelOpen, setHelpPanelOpen] = useState(false)
  let [previewModalOpen, setPreviewModalOpen] = useState(false)
  let [deletingBlockId, setDeletingBlockId] = useState<string | null>(null)
  let [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'info' | 'warning'
  } | null>(null)

  // React Query hooks for server state management
  let queryClient = useQueryClient()
  let {
    data: blocks = [],
    isLoading,
    error,
    isError,
  } = useShowNotesBlocks(showNotesID, basePath)
  let { data: renderedBlocks = [], isLoading: isRenderedBlocksLoading } =
    useShowNotesBlocksRendered(showNotesID, basePath, previewModalOpen)
  let pendingNewBlockType = useRef<'text' | 'image' | 'pdf' | null>(null)
  let createBlockMutation = useCreateBlock(showNotesID, basePath, (type) => {
    // eslint-disable-next-line fp/no-mutation
    pendingNewBlockType.current = type
  })
  let deleteBlockMutation = useDeleteBlock(showNotesID, basePath)
  let reorderBlocksMutation = useReorderBlocks(showNotesID, basePath)

  // Just use server blocks - no optimistic updates
  let displayBlocks = blocks

  // Elegant notification system
  let showNotification = (
    message: string,
    type: 'success' | 'info' | 'warning' = 'info',
  ) => {
    setNotification({ message, type })
    setTimeout(() => setNotification(null), 3000)
  }

  // Notify parent component of block changes
  React.useEffect(() => {
    onBlocksChange?.(blocks)
  }, [blocks, onBlocksChange])

  useEffect(() => {
    if (pendingNewBlockType.current === null) return
    let newBlock = blocks[blocks.length - 1]
    if (newBlock?.id) {
      let label =
        pendingNewBlockType.current === 'text'
          ? 'Text'
          : pendingNewBlockType.current === 'image'
            ? 'Image'
            : 'PDF'
      showNotification(`${label} block created`, 'success')
    }
    // eslint-disable-next-line fp/no-mutation
    pendingNewBlockType.current = null
  }, [blocks])

  let handleCreateBlock = (type: 'text' | 'image' | 'pdf') => {
    setOpenAddBlockAt(null)
    createBlockMutation.mutate(type)
  }

  let handleMoveBlock = (fromIndex: number, toIndex: number) => {
    let moved = displayBlocks[fromIndex]
    let without = displayBlocks.filter((_, i) => i !== fromIndex)
    let reordered = [
      ...without.slice(0, toIndex),
      moved,
      ...without.slice(toIndex),
    ]
    let blockSequences = reordered.map((block, index) => ({
      id: block.id,
      sequence: index,
    }))
    reorderBlocksMutation.mutate(blockSequences)
  }

  let handleBlockChange = (updatedBlock: ShowNotesBlock) => {
    // Optimistically update the React Query cache
    queryClient.setQueryData(
      ['showNotesBlocks', basePath, showNotesID],
      (oldBlocks: ShowNotesBlock[] | undefined) => {
        if (!oldBlocks) return [updatedBlock]
        return oldBlocks.map((block) =>
          block.id === updatedBlock.id ? updatedBlock : block,
        )
      },
    )

    // Also notify parent component
    onBlocksChange?.(
      blocks.map((block) =>
        block.id === updatedBlock.id ? updatedBlock : block,
      ),
    )
  }

  let handleBlockDelete = (blockID: string) => {
    let blockToDelete = displayBlocks.find((b) => b.id === blockID)
    let blockType =
      blockToDelete?.blockType === 'text'
        ? 'Text'
        : blockToDelete?.blockType === 'image'
          ? 'Image'
          : 'PDF'

    // Track which specific block is being deleted
    setDeletingBlockId(blockID)
    showNotification(`Deleting ${blockType.toLowerCase()} block...`, 'warning')

    // Just make the API call - let server delete first, then UI updates
    deleteBlockMutation.mutate(blockID, {
      onSuccess: () => {
        setDeletingBlockId(null)
        showNotification(`${blockType} block deleted`, 'success')
      },
      onError: () => {
        setDeletingBlockId(null)
        showNotification('Failed to delete block', 'warning')
      },
    })
  }

  let handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return
    }

    let sourceIndex = result.source.index
    let destinationIndex = result.destination.index

    if (sourceIndex === destinationIndex) {
      return
    }

    // Use current display blocks for reordering
    let currentBlocks = displayBlocks
    let reorderedBlock = currentBlocks[sourceIndex]
    let blocksWithoutReordered = currentBlocks.filter(
      (_, index) => index !== sourceIndex,
    )
    let newBlocks = [
      ...blocksWithoutReordered.slice(0, destinationIndex),
      reorderedBlock,
      ...blocksWithoutReordered.slice(destinationIndex),
    ]

    // Create sequence mapping for backend
    let blockSequences = newBlocks.map((block, index) => ({
      id: block.id,
      sequence: index,
    }))

    showNotification('Reordering blocks...', 'info')

    // Send reorder request to backend - no optimistic updates
    reorderBlocksMutation.mutate(blockSequences, {
      onSuccess: () => {
        showNotification('Blocks reordered', 'success')
      },
      onError: () => {
        showNotification('Failed to reorder blocks', 'warning')
      },
    })
  }

  if (isLoading) {
    return (
      <Box
        role="status"
        aria-label="Loading content"
        className={className}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8,
        }}
      >
        <CircularProgress aria-hidden="true" />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box className={className}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load content: {error?.message || 'Unknown error'}
        </Alert>
      </Box>
    )
  }

  let validBlocks = displayBlocks.filter((block) => block?.id)

  let existingCitations = Array.from(
    new Set(
      validBlocks
        .filter(
          (block): block is ShowNotesTextBlock => block.blockType === 'text',
        )
        .flatMap((block) => extractCitationTokens(block.markdown))
        .filter((token) => getCitationBody(token).length > 0),
    ),
  )

  return (
    <Box className={className}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<Visibility />}
          onClick={() => setPreviewModalOpen(true)}
          disabled={validBlocks.length === 0}
        >
          Preview
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<HelpOutlined />}
          onClick={() => setHelpPanelOpen(true)}
        >
          Help
        </Button>
      </Box>

      {/* Mutation error indicators */}
      {(createBlockMutation.isError ||
        deleteBlockMutation.isError ||
        reorderBlocksMutation.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {createBlockMutation.isError &&
            `Failed to create block: ${createBlockMutation.error?.message}`}
          {deleteBlockMutation.isError &&
            `Failed to delete block: ${deleteBlockMutation.error?.message}`}
          {reorderBlocksMutation.isError &&
            `Failed to reorder blocks: ${reorderBlocksMutation.error?.message}`}
        </Alert>
      )}

      {validBlocks.length === 0 ? (
        <EmptyState
          onCreateBlock={handleCreateBlock}
          disabled={createBlockMutation.isPending}
        />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="blocks">
            {(provided) => (
              <Box {...provided.droppableProps} ref={provided.innerRef}>
                {validBlocks.map((block, index) => (
                  <Box key={`block-wrapper-${block.id}`}>
                    {reorderBlocksMutation.isPending ? (
                      <BlockSkeleton blockType={block.blockType} />
                    ) : (
                      <BlockContainer
                        block={block}
                        onBlockChange={handleBlockChange}
                        onBlockDelete={handleBlockDelete}
                        onMoveUp={() => handleMoveBlock(index, index - 1)}
                        onMoveDown={() => handleMoveBlock(index, index + 1)}
                        isFirst={index === 0}
                        isLast={index === validBlocks.length - 1}
                        index={index}
                        dialogStatus={dialogStatus}
                        setDialogStatus={setDialogStatus}
                        basePath={basePath}
                        disabled={
                          deleteBlockMutation.isPending ||
                          reorderBlocksMutation.isPending
                        }
                        isDeleting={deletingBlockId === block.id}
                        existingCitations={existingCitations}
                      />
                    )}

                    {/* "+ Add block" pill between/after blocks */}
                    {openAddBlockAt === index ? (
                      <BlockCreationPalette
                        onCreateBlock={handleCreateBlock}
                        onClose={() => setOpenAddBlockAt(null)}
                        disabled={createBlockMutation.isPending}
                        className="mb-4"
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          my: 1,
                        }}
                      >
                        <Button
                          size="small"
                          disabled={
                            createBlockMutation.isPending ||
                            deleteBlockMutation.isPending ||
                            reorderBlocksMutation.isPending
                          }
                          onClick={() => setOpenAddBlockAt(index)}
                          sx={{
                            borderRadius: '999px',
                            border: '1px solid',
                            borderColor: 'divider',
                            color: 'text.secondary',
                            px: 2,
                            py: 0.5,
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: 'text.secondary',
                              bgcolor: 'transparent',
                            },
                          }}
                        >
                          + Add block
                        </Button>
                      </Box>
                    )}
                  </Box>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <EnhancedHelpPanel
        isOpen={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
      />
      <ShowNotesPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        blocks={renderedBlocks.filter((block) => block?.id)}
        showNotesName={showNotesName || 'Show Notes'}
        showNotesDescription={showNotesDescription}
        basePath={basePath}
        loading={isRenderedBlocksLoading}
      />

      {/* Toast notifications */}
      <Snackbar
        open={!!notification}
        autoHideDuration={3000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slots={{ transition: Slide }}
        slotProps={{ transition: { direction: 'up' } as any }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.type || 'info'}
          variant="filled"
          sx={{
            minWidth: '300px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            backdropFilter: 'blur(8px)',
            '& .MuiAlert-icon': { fontSize: '1.2rem' },
          }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default BlockBuilder
